<template>
  <div class="goals-block">
    <h3>{{ currentMonthName }}'s Goals</h3>
    <hr class="divider"/>
    <div class="list-container">
      <div v-if="loading" class="goals-loading">Loading goals...</div>
      <div v-else-if="error" class="goals-error">{{ error }}</div>
      <div v-else>
        <div v-if="goals.length === 0" class="goals-no-items">
          No goals posted yet this month.
        </div>
        <ul v-else class="goals-list">
          <li v-for="goal in goals" :key="goal.id" class="goal-item">
            <div class="goal-icon">
                <i class="fa-light fa-bullseye-arrow"></i>
            </div>
            <div class="goal-details">
              <div class="goal-title">
                {{ goal.title?.rendered || goal.title || 'Untitled Goal' }}
              </div>
              <div class="goal-meta">
                <span>
                  {{ formatCategories(goal) }}
                </span>
              </div>
              <div class="goal-excerpt" v-if="goal.excerpt?.rendered" v-html="truncateContent(goal.excerpt.rendered, 120)"></div>
            </div>
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script>
import { fetchPosts, fetchCurrentUser, fetchCategoriesBySlugs, category_from_role } from '@/utils/fetch.js';

function isThisMonth(dateStr) {
  if (!dateStr) return false;
  const d = new Date(dateStr);
  const now = new Date();
  return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
}

export default {
  name: "GoalsBlock",
  data() {
    return {
      goals: [],
      loading: true,
      error: null,
      currentMonthName: new Date().toLocaleString('default', { month: 'long' }),
      userCategoryIds: [],
    };
  },
  methods: {
    async fetchGoals() {
      this.loading = true;
      this.error = null;

      try {
        // Get user and allowed category IDs
        const user = await fetchCurrentUser();
        const roles = user.my_roles || [];
        const roleCategorySlugs = roles.map(role => category_from_role[role]).filter(Boolean);
        // Always include 'general'
        const allSlugs = [...roleCategorySlugs, 'general'];
        const categories = allSlugs.length ? await fetchCategoriesBySlugs(allSlugs) : [];
        const allowedCategories = categories.map(cat => cat.id);
        this.userCategoryIds = allowedCategories;

        // Fetch all goals for this month
        const posts = await fetchPosts('&per_page=20&_embed', ['goal']);
        const filteredGoals = posts.filter(post =>
          isThisMonth(post.date) &&
          post.categories.some(catId => this.userCategoryIds.includes(catId))
        );
        this.goals = filteredGoals;

        // Cache the results with month/year
        localStorage.setItem('goals-cache', JSON.stringify({
          goals: filteredGoals,
          month: new Date().getMonth(),
          year: new Date().getFullYear()
        }));
      } catch (err) {
        this.error = err.message || 'Failed to fetch goals';
      } finally {
        this.loading = false;
      }
    },
    formatDay(dateStr) {
      const d = new Date(dateStr);
      return d.getDate().toString().padStart(2, '0');
    },
    formatMonth(dateStr) {
      const d = new Date(dateStr);
      return d.toLocaleString('default', { month: 'short' }).toUpperCase();
    },
    truncateContent(html, maxLength = 120) {
      // Remove HTML tags and trim to maxLength
      const text = html.replace(/<[^>]+>/g, '').trim();
      return text.length > maxLength ? text.slice(0, maxLength) + '…' : text;
    },
    formatCategories(goal) {
      // If no categories or only "General", show "General"
      const categories = goal._embedded?.['wp:term']?.[0] || [];
      if (!categories.length) return 'General';
      const names = categories.map(cat => cat.name).filter(Boolean);
      if (names.length === 1 && names[0].toLowerCase() === 'general') return 'General';
      if (names.length === 0) return 'General';
      // If "General" is present with other categories, exclude it
      const filtered = names.filter(name => name.toLowerCase() !== 'general');
      return filtered.length ? filtered.join(' + ') : 'General';
    }
  },
  mounted() {
    this.fetchGoals();
  }
};
</script>

<style scoped>
.goals-block {
  border-radius: 30px;
  padding: 1rem;
  width: 100%;
  text-align: center;
  display: flex;
  flex-direction: column;
  min-height: 0;
  box-sizing: border-box;
  border: solid 1px #D5D1C8;
}
h3 {
  text-align: left;
  font-size: 20px;
  font-weight: 325;
}
.list-container {
  overflow-y: auto;
  overflow-x: hidden;
  height: 100%;
  border-radius: 8px;
}
.goals-list {
  list-style: none;
  padding: 0;
  margin: 0;
  flex: 1 1 0;
  overflow-y: auto;
  min-height: 0;
}
.goal-item {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  background: #fff;
  border-radius: 8px;
  margin-bottom: 1rem;
  text-align: left;
  padding: .5rem 0;
}
.goal-icon {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 2px;
  flex-shrink: 0;
  position: relative;
  font-size: 2em;
  color: var(--primary-color);
}
.date-day {
  font-size: 1.3em;
  line-height: 1;
}
.date-month {
  font-size: 0.85em;
  line-height: 1;
  color: #94918B;
  letter-spacing: 1px;
}
.goal-details {
  flex: 1;
}
.goal-title {
  font-weight: 500;
  font-size: 1.1em;
  color: #5F5E5B;
  margin-bottom: 0.2em;
}
.goal-meta {
  font-size: 0.95em;
  color: #888;
  margin-bottom: 0.3em;
}
.goal-excerpt {
  font-size: 0.98em;
  color: #555;
}
.goals-loading,
.goals-error,
.goals-no-items {
  color: #888;
  font-size: 1em;
  margin: 1em 0;
}
hr.divider {
  border: 0;
  border-top: 1px solid #D5D1C8;
  margin: 5px 0;
}
</style>